import React, { useEffect, useMemo, useState } from "react";
import { Col, Drawer, Flex, Row, Tabs, type TabsProps } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import AxiosInstance from "../../utils/axios";
import { ReactComponent as SeedsIcon } from "../../../assets/images/seeds.svg";
import { ReactComponent as SeedsGrayIcon } from "../../../assets/images/seeds-gray.svg";
import { ReactComponent as NutrientGrayIcon } from "../../../assets/images/soil-gray.svg";
import { ReactComponent as NutrientIcon } from "../../../assets/images/soil.svg";
import { ReactComponent as BiologicalIcon } from "../../../assets/images/bookmarks.svg";
import "./ProtocolDrawer.less";
import LinkedTrialsSection from "./LinkedTrialsSection";
import TrialPreviewDrawer from "../TrialPreviewDrawer";
import {
  getDefaultActiveTabs,
  getProductUnit,
} from "../../../pages/create-protocol/utils";
import { normalizeNutrientCode } from "../../../pages/create-protocol/utils/nutrientNormalization";
import type {
  BiologicalProduct,
  BiologicalProductApplication,
  Objective,
  ProductDetails,
  ProtocolResponse,
} from "../../../pages/new-trail/types";
import { useTranslation } from "react-i18next";
import { formatTranslation } from "../../../shared/utils/translationUtils";
import {
  getNutrientTypeOptions,
  targetsLabels,
} from "../../../pages/create-protocol/constants";

const formatPlotSize = (
  plotSize?: { width?: number | string; length?: number | string } | null
): string => {
  if (
    plotSize?.width !== undefined &&
    plotSize?.width !== null &&
    plotSize?.width !== "" &&
    plotSize?.length !== undefined &&
    plotSize?.length !== null &&
    plotSize?.length !== ""
  ) {
    return `${plotSize.width} x ${plotSize.length}m`;
  }

  return "-";
};

const formatStripWidth = (width?: number | string | null): string => {
  if (width !== undefined && width !== null && width !== "") {
    return `${width} m`;
  }

  return "-";
};

const ProtocolBasicInfoSection: React.FC<{
  protocolData: ProtocolResponse;
  isStripProtocol: boolean;
}> = ({ protocolData, isStripProtocol }) => {
  const { t } = useTranslation();

  return (
    <div className="protocol-basic-info">
      <div className="basic-info-content">
        <div className="info-label">
          {formatTranslation(t("createProtocol.basicInfo.protocolCountry"))}
        </div>
        <div className="info-value">
          {protocolData?.crop_details?.countries[0]}
        </div>
      </div>

      <div className="basic-info-content">
        <div className="info-label">
          {formatTranslation(t("createProtocol.basicInfo.protocolCrop"))}
        </div>
        <div className="info-value">
          <span>{protocolData?.crop}</span>
        </div>
      </div>

      {isStripProtocol ? (
        <>
          <div className="basic-info-content">
            <div className="info-label">
              {formatTranslation(t("createProtocol.basicInfo.stripMinSize"))}
            </div>
            <div className="info-value">
              {formatStripWidth(protocolData?.strip_size?.min_width)}
            </div>
          </div>

          <div className="basic-info-content">
            <div className="info-label">
              {formatTranslation(t("createProtocol.basicInfo.stripMaxSize"))}
            </div>
            <div className="info-value">
              {formatStripWidth(protocolData?.strip_size?.max_width)}
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="basic-info-content">
            <div className="info-label">
              {formatTranslation(t("createProtocol.basicInfo.plotMinimumSize"))}
            </div>
            <div className="info-value">
              {formatPlotSize(protocolData?.plot_minimum_size)}
            </div>
          </div>

          <div className="basic-info-content">
            <div className="info-label">
              {formatTranslation(
                t("createProtocol.basicInfo.plotRecommendedSize")
              )}
            </div>
            <div className="info-value">
              {formatPlotSize(protocolData?.plot_recommended_size)}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const ProtocolDescriptionSection: React.FC<{ description?: string }> = ({
  description,
}) => {
  const { t } = useTranslation();
  const [showFullDescription, setShowFullDescription] = useState(false);

  return (
    <div className="basicinfo-des info-content">
      <h4>
        {formatTranslation(
          t("createProtocol.objectives.objectiveItemDescription")
        )}
      </h4>
      <Flex justify="space-between" gap={8} align="flex-start">
        {description ? (
          <>
            <p>
              {showFullDescription ? description : description.slice(0, 100)}
            </p>
            <button
              type="button"
              className="show-less-button"
              onClick={() => {
                setShowFullDescription((prev) => !prev);
              }}
              disabled={description.length < 100}
            >
              {showFullDescription
                ? formatTranslation(t("common.showLess"))
                : formatTranslation(t("common.showMore"))}
            </button>
          </>
        ) : (
          <p>
            {formatTranslation(t("createProtocol.summary.productDataNotFound"))}
          </p>
        )}
      </Flex>
    </div>
  );
};

const buildObjectiveKey = (
  objective: Objective,
  occurrence: number
): string => {
  const base = [
    objective?.name ?? "",
    objective?.description ?? "",
    objective?.notes ?? "",
    objective?.targets?.join("|") ?? "",
  ].join("::");

  return `${base}-${occurrence}`;
};

const ProtocolObjectiveItem: React.FC<{ objective: Objective }> = ({
  objective,
}) => {
  const { t } = useTranslation();
  const objectiveName = objective?.name ?? "";
  const objectiveWords = objectiveName.trim().split(/\s+/);
  const displayObjectiveName =
    objectiveWords.length > 100
      ? `${objectiveWords.slice(0, 100).join(" ")}...`
      : objectiveName;

  return (
    <div className="objective-content">
      <div className="obj-description-info info-content">
        <h4>
          {formatTranslation(t("createProtocol.objectives.objectiveItemName"))}
        </h4>
        <p>{displayObjectiveName}</p>
      </div>

      <div className="info-content obj-target-info">
        <h4>
          {formatTranslation(
            t("createProtocol.objectives.objectiveItemTarget")
          )}
        </h4>
        <p style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {/* `targets` is optional on the wire, and the objective key builders
              already treat it as such -- reading it unguarded here crashed the
              whole drawer for an objective that came back without one. */}
          {objective?.targets?.map((target: string) => (
            <span
              key={target}
              style={{
                padding: "2px 6px",
                backgroundColor: "#f0f0f0",
                borderRadius: 4,
              }}
            >
              {formatTranslation(t(targetsLabels[target] || target))}
            </span>
          ))}
        </p>
      </div>
    </div>
  );
};

const ProtocolObjectivesSection: React.FC<{ objectives?: Objective[] }> = ({
  objectives,
}) => {
  const { t } = useTranslation();
  const objectiveKeyCount = new Map<string, number>();

  return (
    <div className="protocolo-objectives">
      <h3>{formatTranslation(t("createProtocol.objectives.title"))}</h3>
      {objectives?.map((objective: Objective) => {
        const objectiveKeyBase = [
          objective?.name ?? "",
          objective?.description ?? "",
          objective?.notes ?? "",
          objective?.targets?.join("|") ?? "",
        ].join("::");
        const occurrence = objectiveKeyCount.get(objectiveKeyBase) ?? 0;
        objectiveKeyCount.set(objectiveKeyBase, occurrence + 1);

        return (
          <ProtocolObjectiveItem
            key={buildObjectiveKey(objective, occurrence)}
            objective={objective}
          />
        );
      })}
    </div>
  );
};

const DrawerTitle: React.FC<{
  title?: string;
  showBack: boolean;
  onBack: () => void;
}> = ({ title, showBack, onBack }) => {
  const { t } = useTranslation();

  if (!showBack) return <>{title}</>;

  return (
    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <button
        type="button"
        aria-label={formatTranslation(t("createProtocol.backToProtocol"))}
        onClick={onBack}
        style={{
          background: "none",
          border: "none",
          padding: 0,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
        }}
      >
        <ArrowLeftOutlined />
      </button>
      <span>{title}</span>
    </span>
  );
};

interface DrawerPArams {
  protocolId: string;
  open: boolean;
  onClose: () => void;
  extraHeaders?: {
    "org-id": string;
    "workspace-id": string;
  };
  /**
   * Renders just the protocol content, without the surrounding Drawer. Set by
   * the trial preview, which shows the protocol inside the sidebar it already
   * owns instead of opening a drawer of its own.
   */
  renderInline?: boolean;
  showLinkedTrials?: boolean;
}

const ProtcolDrawer: React.FC<DrawerPArams> = ({
  protocolId,
  open,
  onClose,
  extraHeaders,
  renderInline = false,
  showLinkedTrials,
}) => {
  const { t } = useTranslation();
  const [protocolData, setProtocolData] = useState<ProtocolResponse | null>(
    null
  );

  const [activeTab, setActiveTab] = useState<string>(() =>
    getDefaultActiveTabs([])
  );

  // Which view owns the sidebar: this protocol, or a linked trial opened from
  // the Linked Trials table.
  const [showTrial, setShowTrial] = useState(false);
  const [previewTrial, setPreviewTrial] = useState<{
    id: string;
    name: string;
  } | null>(null);

  // The trial preview can itself drill into its own linked protocol. That
  // second-level view is rendered by the inline TrialPreviewDrawer, but since
  // this drawer owns the title/back-button chrome around it, it needs to know
  // about that navigation to keep them in sync.
  const [nestedProtocol, setNestedProtocol] = useState<{
    id: string;
    name?: string;
  } | null>(null);
  const [trialBackSignal, setTrialBackSignal] = useState(0);

  const getProtocolData = async (): Promise<any> => {
    try {
      const params = showLinkedTrials
        ? new URLSearchParams([
            ["scope", "trial_insights"],
            ["scope", "farm_count"],
            ["scope", "field_count"],
            ["scope", "linked_trials"],
          ])
        : undefined;
      const response = await AxiosInstance.get(`/protocol/${protocolId}`, {
        headers: extraHeaders,
        params,
      });
      setProtocolData(response?.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    if (protocolId) {
      void getProtocolData();
    }
  }, [protocolId, showLinkedTrials]);

  // A freshly opened sidebar always starts on the protocol, never on a trial
  // (or that trial's own linked protocol) a previous visit left behind.
  useEffect(() => {
    if (open) {
      setShowTrial(false);
      setNestedProtocol(null);
    }
  }, [open, protocolId]);

  const handleDrawerBack = (): void => {
    if (nestedProtocol) {
      setNestedProtocol(null);
      setTrialBackSignal((count) => count + 1);
      return;
    }
    setShowTrial(false);
  };

  const getDrawerTitle = (): string | undefined => {
    if (nestedProtocol) return nestedProtocol.name ?? nestedProtocol.id;
    if (showTrial && previewTrial) return previewTrial.name;
    return protocolData?.name;
  };

  useEffect(() => {
    if (protocolData?.protocol_type?.length) {
      setActiveTab(getDefaultActiveTabs(protocolData.protocol_type));
    }
  }, [protocolData?.protocol_type]);

  const items: TabsProps["items"] = useMemo(
    () =>
      [
        {
          key: "seed",
          label: formatTranslation(t("ratesDosage.tabs.seeds")),
          children: (
            <ProtocolSeedsComponent
              seeds={protocolData?.product_details?.seeds}
            />
          ),
          icon: activeTab === "seed" ? <SeedsIcon /> : <SeedsGrayIcon />,
        },
        {
          key: "biological",
          label: formatTranslation(
            t("createProtocol.products.tabs.biologicals")
          ),
          children: (
            <ProtocolBiologicalsComponent
              biologicals={protocolData?.product_details?.biologicals}
            />
          ),
          icon: <BiologicalIcon />,
        },
        {
          key: "nutrient",
          label: formatTranslation(
            t("createProtocol.products.nutrients.header")
          ),
          children: (
            <ProtocolNutrientsComponent
              nutrients={protocolData?.product_details?.nutrients}
            />
          ),
          icon:
            activeTab === "nutrient" ? <NutrientIcon /> : <NutrientGrayIcon />,
        },
      ].filter((tab) => protocolData?.protocol_type?.includes(tab?.key)),
    [activeTab, protocolData?.product_details, protocolData?.protocol_type]
  );

  const trialApplicationMethod = Array.isArray(
    (protocolData as any)?.trial_application_method
  )
    ? (protocolData as any)?.trial_application_method?.[0]
    : protocolData?.trial_application_method;

  const isStripProtocol = trialApplicationMethod === "strip";

  const content = protocolData ? (
    <div className="protocol-drawer-container">
      <ProtocolBasicInfoSection
        protocolData={protocolData}
        isStripProtocol={isStripProtocol}
      />

      <ProtocolDescriptionSection description={protocolData?.description} />

      <ProtocolObjectivesSection objectives={protocolData?.objectives} />

      <div className="protocol-product-info">
        <Tabs activeKey={activeTab} items={items} onChange={setActiveTab} />
      </div>

      {showLinkedTrials && (
        <LinkedTrialsSection
          trialInsights={protocolData?.trial_insights}
          farmCount={protocolData?.farm_count}
          fieldCount={protocolData?.field_count}
          linkedTrials={protocolData?.linked_trials}
          onTrialClick={(trialId, trialName) => {
            setPreviewTrial({ id: trialId, name: trialName });
            setShowTrial(true);
          }}
        />
      )}
    </div>
  ) : null;

  // The trial preview hosts this content inside its own sidebar, so the drawer
  // shell is skipped there: one panel whose content is swapped, instead of a
  // second drawer sliding in over the first.
  if (renderInline) return content;

  return (
    <div>
      {protocolData && (
        <Drawer
          title={
            <DrawerTitle
              title={getDrawerTitle()}
              showBack={
                Boolean(nestedProtocol) || (showTrial && Boolean(previewTrial))
              }
              onBack={handleDrawerBack}
            />
          }
          placement="right"
          width={500}
          open={open}
          onClose={onClose}
        >
          {showTrial && previewTrial ? (
            <TrialPreviewDrawer
              trialId={previewTrial.id}
              open
              onClose={onClose}
              extraHeaders={extraHeaders}
              renderInline
              onViewProtocol={(id, name) => {
                setNestedProtocol({ id, name });
              }}
              collapseProtocolViewSignal={trialBackSignal}
            />
          ) : (
            content
          )}
        </Drawer>
      )}
    </div>
  );
};

const normalizeDensityOptionsToStrings = (raw: unknown): string[] => {
  if (!raw) return [];

  if (Array.isArray(raw)) {
    return raw
      .map((opt: any) => {
        if (typeof opt === "number" || typeof opt === "string")
          return String(opt);

        if (
          opt &&
          (typeof opt.value === "number" || typeof opt.value === "string")
        ) {
          return opt.label ? String(opt.label) : String(opt.value);
        }

        if (
          opt &&
          (typeof opt.min === "number" || typeof opt.min === "string") &&
          (typeof opt.max === "number" || typeof opt.max === "string")
        ) {
          return `${String(opt.min)}-${String(opt.max)}`;
        }

        return "";
      })
      .filter(Boolean);
  }

  if (typeof raw === "number" || typeof raw === "string") {
    return [String(raw)];
  }

  return [];
};

const ProtocolSeedsComponent: React.FC<ProductDetails> = ({ seeds }) => {
  const { t } = useTranslation();

  const globalDensityOptionsStrings = useMemo(
    () => normalizeDensityOptionsToStrings((seeds as any)?.density_options),
    [seeds]
  );

  const hasGlobalDensityRange =
    Boolean((seeds as any)?.global_density_range) &&
    typeof (seeds as any)?.density_range?.min === "number" &&
    typeof (seeds as any)?.density_range?.max === "number";

  const shouldShowGlobalDensityOptions =
    Boolean((seeds as any)?.global_density_range) &&
    !hasGlobalDensityRange &&
    globalDensityOptionsStrings.length > 0;

  const hasAnyProductOptions = useMemo(() => {
    const products = (seeds as any)?.products;
    if (!Array.isArray(products)) return false;
    return products.some(
      (p: any) =>
        Array.isArray(p?.density_options) && p.density_options.length > 0
    );
  }, [seeds]);

  const densityHeaderKey =
    shouldShowGlobalDensityOptions || hasAnyProductOptions
      ? "createProtocol.products.densityOption"
      : "createProtocol.products.densityRange";

  return (
    <div className="product-seeds">
      <h3>{formatTranslation(t("createProtocol.products.rightTilte"))}</h3>
      <div className="seeds-info">
        <Row className="seed-table-header">
          <Col span={12}>
            <h4>
              {formatTranslation(t("createProtocol.products.rightTilte"))}
            </h4>
          </Col>
          <Col span={12}>
            <h4>{formatTranslation(t(densityHeaderKey))}</h4>
          </Col>
        </Row>

        {seeds ? (
          (seeds as any)?.products?.map((product: any) => {
            const productRangeMin = product?.density_range?.min;
            const productRangeMax = product?.density_range?.max;

            const hasProductRange =
              (typeof productRangeMin === "number" ||
                typeof productRangeMin === "string") &&
              (typeof productRangeMax === "number" ||
                typeof productRangeMax === "string");

            const productOptionsStrings = normalizeDensityOptionsToStrings(
              product?.density_options
            );
            const hasProductOptions = productOptionsStrings.length > 0;
            let densityValue: React.ReactNode;

            if (hasGlobalDensityRange) {
              densityValue = (
                <p className="summary-card-dynamic-text">
                  {(seeds as any)?.density_range?.min} -{" "}
                  {(seeds as any)?.density_range?.max} {seeds?.measure_unit}
                </p>
              );
            } else if (shouldShowGlobalDensityOptions) {
              densityValue = (
                <p className="summary-card-dynamic-text">
                  {globalDensityOptionsStrings.join(", ")} {seeds?.measure_unit}
                </p>
              );
            } else if (hasProductOptions) {
              densityValue = (
                <p className="summary-card-dynamic-text">
                  {productOptionsStrings.join(", ")} {seeds?.measure_unit}
                </p>
              );
            } else if (hasProductRange) {
              densityValue = (
                <p className="summary-card-dynamic-text">
                  {productRangeMin} - {productRangeMax} {seeds?.measure_unit}
                </p>
              );
            } else {
              densityValue = (
                <p className="summary-card-dynamic-text">
                  {formatTranslation(
                    t("createProtocol.summary.productDataNotFound")
                  )}
                </p>
              );
            }
            const productKey =
              product?.id ??
              JSON.stringify({
                variety: product?.variety,
                densityRange: product?.density_range,
                densityOptions: productOptionsStrings,
              });

            return (
              <Row key={productKey} className="products-row">
                <Col span={12}>
                  <p className="summary-card-dynamic-text">
                    {product?.variety ?? "-"}
                  </p>
                </Col>
                <Col span={12}>{densityValue}</Col>
              </Row>
            );
          })
        ) : (
          <Row className="text-center">
            <Col>
              <p>
                {formatTranslation(
                  t("createProtocol.summary.productDataNotFound")
                )}
              </p>
            </Col>
          </Row>
        )}
      </div>
    </div>
  );
};

const ProtocolNutrientsComponent: React.FC<ProductDetails> = ({
  nutrients,
}) => {
  const { t } = useTranslation();

  const findNutrientLabel = (value: string): string => {
    // Normalize input to handle legacy/dynamic formats from backend
    const normalizedValue = normalizeNutrientCode(value);
    if (!normalizedValue) return "";

    for (const category of getNutrientTypeOptions()) {
      const nutrient = category.options.find(
        (option: { value: string; label: string }) =>
          option.value === normalizedValue
      );
      if (nutrient) return nutrient.label;
    }
    return "";
  };

  return (
    <div className="product-seeds">
      <h3>
        {formatTranslation(t("createProtocol.products.nutrients.header"))}
      </h3>
      {nutrients ? (
        <>
          <div className="info-content">
            <h4>
              {formatTranslation(
                t("createProtocol.summary.nutrients.basicNutrient")
              )}
            </h4>
            <p>{findNutrientLabel(nutrients?.products?.[0]?.nutrient_code)}</p>
          </div>

          <div className="protocol-basic-info">
            <div className="basic-info-content">
              <div className="info-label">
                {formatTranslation(
                  t("createProtocol.summary.nutrients.applicationRange")
                )}
              </div>
              <div className="info-value">
                {nutrients?.application_range?.min ?? "-"} -{" "}
                {nutrients?.application_range?.max ?? "-"} %
              </div>
            </div>
            <div className="basic-info-content">
              <div className="info-label">
                {formatTranslation(
                  t("createProtocol.summary.nutrients.minDistanceUnit")
                )}
              </div>
              <div className="info-value">{nutrients?.interval_distance}%</div>
            </div>
            <div className="basic-info-content">
              <div className="info-label">
                {formatTranslation(
                  t("createProtocol.summary.nutrients.noOfIntervals")
                )}
              </div>
              <div className="info-value">{nutrients?.number_of_intervals}</div>
            </div>
          </div>
        </>
      ) : (
        <p>
          {formatTranslation(t("createProtocol.summary.productDataNotFound"))}
        </p>
      )}
    </div>
  );
};

const ProtocolBiologicalsComponent: React.FC<any> = ({ biologicals }) => {
  const { t } = useTranslation();

  return (
    <div className="product-seeds">
      {biologicals ? (
        <>
          {biologicals?.treatments?.map((product: any, index: number) => (
            <React.Fragment
              key={`${String(product?.treatmentId ?? "treatment")}-${index}`}
            >
              {index >= 1 ? <div className="basicinfo-heading-border" /> : null}

              <div className="info-content">
                <h3>
                  {formatTranslation(
                    t(
                      "createProtocol.products.biologicals.product.treatmentTitle"
                    )
                  )}{" "}
                  {index + 1}
                </h3>
              </div>

              <div className="protocol-treatment-info">
                <div className="basic-info-content">
                  <div className="info-label">
                    <div className="protocol-info-style">
                      {formatTranslation(
                        t("createProtocol.products.rightTilte")
                      )}
                    </div>
                  </div>

                  {product.products?.map((item: BiologicalProduct) => (
                    <div className="info-value" key={item.id}>
                      {item?.variety} {item?.rate}{" "}
                      {getProductUnit(item?.measure_unit)}
                    </div>
                  ))}

                  <div>
                    <div className="info-label">
                      {formatTranslation(
                        t(
                          "createProtocol.products.biologicals.product.instructions"
                        )
                      )}{" "}
                    </div>
                  </div>
                  <div className="info-value">
                    {typeof product?.instructions === "string"
                      ? product.instructions
                      : formatTranslation(
                          t("newTrial.ratesAndDosage.notApplicable")
                        )}
                  </div>
                </div>

                <div className="basic-info-content">
                  <div className="info-label">
                    <div className="protocol-info-style">
                      {formatTranslation(
                        t(
                          "createProtocol.products.biologicals.product.tankMixRate"
                        )
                      )}
                    </div>
                  </div>
                  <div className="info-value">
                    {typeof product?.tank_mix_rate?.min === "number" &&
                    typeof product?.tank_mix_rate?.max === "number"
                      ? formatTranslation(
                          t(
                            "createProtocol.summary.biological.tankMixRateFormat",
                            {
                              min: String(product.tank_mix_rate.min),
                              max: String(product.tank_mix_rate.max),
                            }
                          )
                        )
                      : formatTranslation(
                          t("newTrial.ratesAndDosage.notApplicable")
                        )}
                  </div>
                </div>
              </div>
            </React.Fragment>
          ))}

          {biologicals?.applications?.length > 0 ? (
            <>
              <div className="basicinfo-des info-content" />
              <h3>{formatTranslation(t("createProtocol.summary.title5"))} </h3>

              {biologicals?.applications?.map(
                (application: BiologicalProductApplication, index: number) => (
                  <div
                    key={application?.description ?? index}
                    className="info-content"
                  >
                    <h4>
                      {formatTranslation(
                        t("createProtocol.summary.biological.application")
                      )}{" "}
                      {index + 1}
                    </h4>
                    <p>{application?.description}</p>
                  </div>
                )
              )}
            </>
          ) : null}
        </>
      ) : (
        <p>
          {formatTranslation(t("createProtocol.summary.productDataNotFound"))}
        </p>
      )}
    </div>
  );
};

export default ProtcolDrawer;
